
    import __i18nConfig from '@next-translate-root/i18n'
    import __appWithI18n from 'next-translate/appWithI18n'
    import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Jost, Noto_Sans } from 'next/font/google';
import React from 'react';
import { registerUspApi } from '@/features/gpc';
import { Providers } from '@/components/modules/Providers';
import { useSetDirection } from '@/hooks/useSetDirection';
import { useViewportHeight } from '@/hooks/useViewportHeight';
import { FacebookService } from '@/services/facebookPixelService';
import { GoogleService } from '@/services/googleService';
import { buildLocaleHref } from '@/helpers/buildLocaleHref';
import { getLocaleFromPath } from '@/helpers/getLocaleFromPath';
import { seoData } from '@/data/seoData';
import { NODE_ID_KEYS, PROJECT_TYPE, VALID_LOCALES_SET } from '@/constants';
import Icon from '@/public/icons/arrow-thin.svg';
import '@/styles/globals.scss';
const DefaultSeo = dynamic(async () => await import('next-seo').then(m => m.DefaultSeo));
const handleRouteChange = (url: string) => {
    GoogleService.GTMPageview(url);
    FacebookService.logEvent('PageView');
};
const getPathnameNoQuery = (asPath?: string) => asPath ? asPath.split('?')[0] ?? '/' : '/';
const getCurrentBrowserPath = (fallbackPath?: string) => {
    if (typeof window !== 'undefined') {
        return window.location.pathname || fallbackPath || '/';
    }
    return fallbackPath ?? '/';
};
const getCurrentBrowserSearch = () => {
    if (typeof window !== 'undefined') {
        return window.location.search || '';
    }
    return '';
};
const normalizeLocale = (rawLocale?: string): string | undefined => {
    if (!rawLocale) {
        return undefined;
    }
    const normalizedLocale = rawLocale.toLowerCase();
    return VALID_LOCALES_SET.has(normalizedLocale) ? normalizedLocale : undefined;
};
const MyApp = ({ Component, pageProps, router }: AppProps) => {
    useViewportHeight();
    const routePath = getPathnameNoQuery(router.asPath);
    const pageLocale = typeof pageProps?.locale === 'string'
        ? normalizeLocale(pageProps.locale)
        : undefined;
    const currentLocale = normalizeLocale(router.locale) ??
        pageLocale ??
        getLocaleFromPath(routePath);
    const { title: unusedTitle, openGraph, ...defaultSeoData } = seoData;
    const { title: unusedOgTitle, ...defaultOpenGraph } = openGraph;
    useSetDirection(currentLocale);
    const onRouteChangeComplete = React.useCallback((url: string) => {
        handleRouteChange(url);
        if (typeof document !== 'undefined' && document.documentElement) {
            document.documentElement.lang = getLocaleFromPath(window.location.pathname || '/');
        }
    }, []);
    // Register the IAB US Privacy (__uspapi) stub immediately on load. The USP
    // string is computed lazily, so it reflects geo once the lookup resolves.
    React.useEffect(() => {
        registerUspApi();
    }, []);
    React.useEffect(() => {
        if (typeof document !== 'undefined' && document.documentElement) {
            document.documentElement.lang = getLocaleFromPath(window.location.pathname || '/');
        }
        router.events.on('routeChangeComplete', onRouteChangeComplete);
        return () => {
            router.events.off('routeChangeComplete', onRouteChangeComplete);
        };
    }, [router.events, onRouteChangeComplete]);
    const handleDebugLocaleChange = React.useCallback((nextLocaleRaw: string) => {
        const nextLocale = nextLocaleRaw.toLowerCase();
        if (!VALID_LOCALES_SET.has(nextLocale)) {
            return;
        }
        const pathname = getCurrentBrowserPath(getPathnameNoQuery(router.asPath));
        const search = getCurrentBrowserSearch();
        const targetPath = buildLocaleHref(pathname, nextLocale, {
            fallbackToHomeOnNonLocalized: true,
        });
        const nextHref = `${targetPath}${search}`;
        if (nextHref === router.asPath) {
            return;
        }
        void router.push(nextHref);
    }, [router]);
    const dynamicSeoData = {
        ...defaultSeoData,
        openGraph: {
            ...defaultOpenGraph,
            locale: currentLocale,
        },
    };
    return (<div id={NODE_ID_KEYS.APP} className={`app ${notoSans.variable} ${jost.variable}`}>
			<DefaultSeo {...dynamicSeoData}/>

			<Providers>
				<Component {...pageProps}/>
			</Providers>

			{!PROJECT_TYPE.PROD ? (<div className='locale'>
					{router?.locales?.map(locale => (<button key={locale} type='button' className={`locale__button ${currentLocale === locale ? 'locale__button_is-active' : ''}`} onClick={() => handleDebugLocaleChange(locale)}>
							{locale}
						</button>))}
					<div className='locale__button'>
						<Icon className='locale__button-icon'/>
					</div>
				</div>) : null}
		</div>);
};
export const notoSans = Noto_Sans({
    weight: ['400', '500', '600', '700', '800', '900'],
    subsets: ['cyrillic', 'latin'],
    variable: '--notoSans-font',
});
export const jost = Jost({
    weight: ['400', '500', '600', '700'],
    subsets: ['latin'],
    variable: '--jost-font',
});
const __Next_Translate__Page__1a1abf48a4c__ = MyApp;

    export default __appWithI18n(__Next_Translate__Page__1a1abf48a4c__, {
      ...__i18nConfig,
      isLoader: true,
      skipInitialProps: true,
      loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
    });
  